import { useState } from "react";
import { fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TriangleAlert, CheckCircle2, ImageOff, Eye, Trash2, ArrowRightLeft, Pencil } from "lucide-react";

const KIND_COLOR = { attraction: "#10B981", advertiser: "#F59E0B", detour: "#8B5CF6", route: "#22D3EE" };

function Thumb({ item }) {
  return <div className="w-9 h-9 rounded-md overflow-hidden bg-secondary flex items-center justify-center shrink-0">{item.photos?.[0] ? <img src={fileUrl(item.photos[0])} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-4 h-4 text-muted-foreground" />}</div>;
}

export function ReassignPreviewModal({ preview, onCancel, onConfirm, onViewSkipped }) {
  return (
    <Dialog open={!!preview} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-lg" data-testid="reassign-preview-modal">
        <DialogHeader><DialogTitle>Reassign Preview</DialogTitle></DialogHeader>
        {preview && (<>
          <p className="text-sm text-muted-foreground -mt-2">Review how selected items align with the new zone before confirming.</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-secondary p-3"><div className="text-2xl font-bold" data-testid="preview-total">{preview.total}</div><div className="text-xs text-muted-foreground">Selected</div></div>
            <div className="rounded-lg bg-primary/10 p-3"><div className="text-2xl font-bold text-primary" data-testid="preview-inside">{preview.inside}</div><div className="text-xs text-muted-foreground">Will move</div></div>
            <div className="rounded-lg bg-red-500/10 p-3"><div className="text-2xl font-bold text-red-500" data-testid="preview-outside">{preview.outside}</div><div className="text-xs text-muted-foreground">Skipped</div></div>
          </div>
          {preview.items?.length > 0 && (
            <div className="max-h-52 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="preview-outside-list">
              <div className="px-3 py-1.5 text-xs font-semibold text-red-500 flex items-center gap-1"><TriangleAlert className="w-3.5 h-3.5" />Outside new zone boundary</div>
              {preview.items.map((it) => (
                <div key={it.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <Thumb item={it} /><span className="flex-1 truncate">{it.name}</span>
                  <span className="px-2 py-0.5 rounded-full text-xs text-white" style={{ background: KIND_COLOR[it.kind] }}>{it.kind}</span>
                </div>
              ))}
            </div>
          )}
          {preview.outside === 0 && <div className="flex items-center gap-2 text-sm text-primary"><CheckCircle2 className="w-4 h-4" />All selected items fall inside the new zone.</div>}
        </>)}
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={onCancel} data-testid="preview-cancel">Cancel</Button>
          {preview?.outside > 0 && <Button variant="outline" onClick={onViewSkipped} data-testid="preview-view-skipped">View Skipped Items</Button>}
          <Button onClick={onConfirm} data-testid="preview-confirm">Confirm Reassign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SkippedReportModal({ report, zones, onClose, onReassign, onHide, onDelete, onFixAll }) {
  const [pick, setPick] = useState({});
  return (
    <Dialog open={!!report} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="skipped-report-modal">
        <DialogHeader><DialogTitle>Skipped Items Report</DialogTitle></DialogHeader>
        {report && (<>
          <p className="text-sm text-muted-foreground -mt-2">Some items could not be reassigned because they fall outside the selected zone.</p>
          <div className="flex gap-4 text-sm">
            <span>Total: <b data-testid="report-total">{report.total}</b></span>
            <span className="text-primary">Reassigned: <b data-testid="report-affected">{report.affected}</b></span>
            <span className="text-red-500">Skipped: <b data-testid="report-skipped">{report.items.length}</b></span>
          </div>
          <div className="max-h-72 overflow-y-auto rounded-lg border border-border divide-y divide-border">
            {report.items.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground" data-testid="report-empty">All skipped items handled.</div> : report.items.map((it) => (
              <div key={it.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" data-testid={`report-row-${it.id}`}>
                <Thumb item={it} />
                <div className="flex-1 min-w-[120px]"><div className="font-medium truncate">{it.name}</div><div className="text-xs text-muted-foreground">{it.kind} · {it.shop_name || "—"} · {it.zone_name || "—"}</div></div>
                <Select value={pick[it.id] || ""} onValueChange={(v) => setPick({ ...pick, [it.id]: v })}>
                  <SelectTrigger className="w-36 h-8" data-testid={`report-zone-${it.id}`}><SelectValue placeholder="Zone…" /></SelectTrigger>
                  <SelectContent>{zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" disabled={!pick[it.id]} onClick={() => onReassign(it.id, pick[it.id])} data-testid={`report-reassign-${it.id}`}><ArrowRightLeft className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => onHide(it.id)} data-testid={`report-hide-${it.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(it.id)} data-testid={`report-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        </>)}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="report-close">Close</Button>
          <Button disabled={!report?.items.length} onClick={onFixAll} data-testid="report-fix-all"><Pencil className="w-4 h-4 mr-1" />Fix All (Override)</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
